import {
  APIAggregatesResponse,
  APIDashboardAttributes,
  APIDashboardFilterSuggestionsResponse,
  APIHierarchicalDashboardFilterSuggestion,
  APIPresentationAssetAttributes,
  APIPresentationAttributes,
  APIReportColumnSuggestions,
  APIReportFilters,
  APISearchResponse,
  APISlideAttributes,
  ArdoqId,
  DashboardFilterValues,
  SortOrder,
  ViewIds,
} from '@ardoq/api-types';
import { ApiResponse } from './types';
import {
  crudApi,
  fetchJson,
  headers,
  Method,
  renameEntity,
  toRequestOptions,
} from './api';

const toAggregatedSearchParams = (
  contextComponentId: ArdoqId | null,
  viewsToConvert: ViewIds[] | null
): string => {
  const searchParams = new URLSearchParams({
    ...(contextComponentId && { context: contextComponentId }),
    ...(viewsToConvert && { convertViews: viewsToConvert.join(',') }),
  }).toString();
  if (!searchParams) return '';
  return `?${searchParams}`;
};

/**
 * `viewsToConvert` lists the views whose workspace-mode slides the backend
 * converts to viewpoint mode; null leaves it to the organization's features.
 */
const fetchAggregated = (
  presentationId: ArdoqId,
  contextComponentId: ArdoqId | null,
  viewsToConvert: ViewIds[] | null
): ApiResponse<APIPresentationAttributes> => {
  const request = new Request(
    `/api/presentation/${presentationId}/aggregated${toAggregatedSearchParams(
      contextComponentId,
      viewsToConvert
    )}`,
    { headers }
  );
  return fetchJson<APIPresentationAttributes>(request);
};

export type CanCreatorMigrateSlidesInfo = Record<
  string,
  { creatorCanMigrate: boolean }
>;

const migrationStatus = (
  presentationId: ArdoqId,
  slideIds: ArdoqId[]
): ApiResponse<CanCreatorMigrateSlidesInfo> => {
  const request = new Request(
    `/api/presentation/${presentationId}/slide-migration-status`,
    toRequestOptions(Method.POST, { slideIds })
  );
  return fetchJson<CanCreatorMigrateSlidesInfo>(request);
};

const fetchSlides = async (
  presentationId: ArdoqId
): ApiResponse<APISlideAttributes[]> => {
  const request = new Request(`/api/presentation/${presentationId}/slides`, {
    headers,
  });
  return fetchJson<APISlideAttributes[]>(request);
};

const copy = (
  presentationId: ArdoqId,
  name: string
): ApiResponse<APIPresentationAssetAttributes> => {
  const request = new Request(
    `/api/presentation/${presentationId}/copy`,
    toRequestOptions(Method.POST, { name })
  );
  return fetchJson<APIPresentationAssetAttributes>(request);
};
type SearchBody = {
  from: number;
  size: number;
  sort: string | undefined;
  order: SortOrder | undefined;
  filters: APIReportFilters | undefined;
};

const search = async (
  presentationId: ArdoqId,
  reportId: ArdoqId,
  slideId: ArdoqId,
  body: SearchBody
): ApiResponse<APISearchResponse> => {
  const request = new Request(
    `/api/presentation/${presentationId}/slide/${slideId}/report/${reportId}`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<APISearchResponse>(request);
};

const fetchReportColumnSuggestions = (
  presentationId: ArdoqId,
  reportId: ArdoqId,
  slideId: ArdoqId,
  columnKey: string,
  query: string,
  limit: number = 100
): ApiResponse<APIReportColumnSuggestions> => {
  const request = new Request(
    `/api/presentation/${presentationId}/slide/${slideId}/report/${reportId}/column/${columnKey}/suggestion?q=${query}&limit=${limit}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<APIReportColumnSuggestions>(request);
};

/**
 * @deprecated use fetchDashboardSlide instead, this api does not respect the applied dashboard filter values on the dashboard slide
 */
const fetchDashboard = async ({
  dashboardId,
  presentationId,
}: {
  dashboardId: ArdoqId;
  presentationId: ArdoqId;
}): ApiResponse<APIDashboardAttributes> => {
  const request = new Request(
    `/api/presentation/${presentationId}/dashboard/v3/${dashboardId}`,
    { headers }
  );

  return fetchJson<APIDashboardAttributes>(request);
};

type DashboardSlidePath = {
  dashboardId: ArdoqId;
  presentationId: ArdoqId;
  slideId: ArdoqId;
};

const toDashboardSlideUrl = ({
  presentationId,
  slideId,
  dashboardId,
}: DashboardSlidePath) =>
  `/api/presentation/${presentationId}/slide/${slideId}/dashboard/${dashboardId}`;

// only this api would respect the applied global filter values on the dashboard slide
const fetchDashboardSlide = async ({
  globalFilterValues,
  ...path
}: DashboardSlidePath & {
  globalFilterValues?: DashboardFilterValues;
}): ApiResponse<APIDashboardAttributes> => {
  const request = new Request(
    toDashboardSlideUrl(path),
    globalFilterValues
      ? toRequestOptions(Method.POST, { globalFilterValues })
      : { headers }
  );
  return fetchJson<APIDashboardAttributes>(request);
};

type FetchDashboardSlideFilterSuggestionsParams = DashboardSlidePath & {
  parameterId: ArdoqId;
  globalFilterValues?: DashboardFilterValues;
} & (
    | { isWithHierarchy: true }
    | { isWithHierarchy?: false; query: string; limit?: number }
  );

function fetchDashboardSlideFilterSuggestions(
  params: FetchDashboardSlideFilterSuggestionsParams & { isWithHierarchy: true }
): ApiResponse<
  APIDashboardFilterSuggestionsResponse<APIHierarchicalDashboardFilterSuggestion>
>;
function fetchDashboardSlideFilterSuggestions(
  params: FetchDashboardSlideFilterSuggestionsParams & {
    isWithHierarchy?: false;
  }
): ApiResponse<APIDashboardFilterSuggestionsResponse>;
function fetchDashboardSlideFilterSuggestions(
  params: FetchDashboardSlideFilterSuggestionsParams
): ApiResponse<APIDashboardFilterSuggestionsResponse> {
  const searchParams = params.isWithHierarchy
    ? new URLSearchParams({ hierarchy: 'true' })
    : new URLSearchParams({
        q: params.query,
        limit: (params.limit ?? 100).toString(),
      });
  const request = new Request(
    `${toDashboardSlideUrl(params)}/filter-parameter/${params.parameterId}/suggestion?${searchParams.toString()}`,
    toRequestOptions(Method.POST, {
      globalFilterValues: params.globalFilterValues,
    })
  );
  return fetchJson<APIDashboardFilterSuggestionsResponse>(request);
}

const addSlide = async (
  presentationId: ArdoqId,
  slide: APISlideAttributes
): ApiResponse<{
  presentation: APIPresentationAssetAttributes;
  slide: APISlideAttributes;
}> => {
  const request = new Request(
    `/api/presentation/${presentationId}/add-slide/`,
    toRequestOptions(Method.POST, slide)
  );
  return fetchJson<{
    presentation: APIPresentationAssetAttributes;
    slide: APISlideAttributes;
  }>(request);
};

const duplicateSlide = async (
  presentationId: ArdoqId,
  slideId: ArdoqId
): ApiResponse<{
  presentation: APIPresentationAttributes;
  slide: APISlideAttributes;
}> => {
  const request = new Request(
    `/api/presentation/${presentationId}/duplicate-slide/`,
    toRequestOptions(Method.POST, { slideId })
  );
  return fetchJson<{
    presentation: APIPresentationAttributes;
    slide: APISlideAttributes;
  }>(request);
};

const fetchReportAggregates = (
  presentationId: ArdoqId,
  slideId: ArdoqId,
  reportId: ArdoqId,
  filters?: APIReportFilters
): ApiResponse<APIAggregatesResponse> => {
  const request = new Request(
    `/api/presentation/${presentationId}/slide/${slideId}/report/${reportId}/aggregates`,
    toRequestOptions(Method.POST, { filters })
  );
  return fetchJson<APIAggregatesResponse>(request);
};

export const presentationApi = {
  fetchAggregated,
  migrationStatus,
  fetchSlides,
  copy,
  search,
  fetchReportColumnSuggestions,
  addSlide,
  duplicateSlide,
  fetchDashboard,
  fetchDashboardSlide,
  fetchDashboardSlideFilterSuggestions,
  rename: renameEntity<APIPresentationAssetAttributes>('presentation'),
  fetchReportAggregates,
  ...crudApi<APIPresentationAssetAttributes>('presentation'),
};
